/**
 * Admin API: designs of every account, read-only.
 */
import apiClient, { ApiResponse } from '../client';
import type { AdminProductListResult } from './products';

export type AdminDesignStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

export const ADMIN_DESIGN_STATUSES: AdminDesignStatus[] = [
  'DRAFT',
  'ACTIVE',
  'ARCHIVED',
];

export type AdminMockupStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED';

export interface AdminDesignOwner {
  id: string;
  email: string;
  shirteeDesignerId: number | null;
}

/** Head fields shared by the list row and the detail view. */
export interface AdminDesignHead {
  id: string;
  sku: string;
  name: string;
  status: AdminDesignStatus;
  createdVia: string | null;
  publishedAt: string | null;
  createdAt: string;
  masterProductId: string | null;
  externalDesignerId: number | null;
  owner: AdminDesignOwner;
}

/** Mockup state of the enabled products of a design. */
export interface AdminDesignMockupCounts {
  completed: number;
  failed: number;
  expected: number;
}

export interface AdminDesignListItem extends AdminDesignHead {
  mockups: AdminDesignMockupCounts;
}

export interface AdminDesignQuery {
  ownerUserId?: string;
  externalDesignerId?: number;
  status?: AdminDesignStatus;
  /** ISO date (YYYY-MM-DD), inclusive. */
  createdFrom?: string;
  /** ISO date (YYYY-MM-DD), inclusive: covers the whole UTC day. */
  createdTo?: string;
  page?: number;
  limit?: number;
}

export interface AdminDesignListResult {
  data: AdminDesignListItem[];
  meta: AdminProductListResult['meta'];
}

export interface AdminDesignMockup {
  positionSlug: string;
  status: AdminMockupStatus;
  imageUrl: string | null;
  errorMessage: string | null;
}

export interface AdminDesignColor {
  productColorId: string;
  name: string;
  hexCode: string;
  mockups: AdminDesignMockup[];
}

export interface AdminDesignPosition {
  slug: string;
  isCustomPlacement: boolean;
  designCropUrl: string | null;
}

export interface AdminDesignProduct {
  id: string;
  productId: string;
  artNumber: string | null;
  productName: string;
  isEnabled: boolean;
  positions: AdminDesignPosition[];
  colors: AdminDesignColor[];
}

export interface AdminDesignDetail extends AdminDesignHead {
  products: AdminDesignProduct[];
}

/**
 * List designs across all accounts, newest first, with their mockup state
 */
export async function getAdminDesigns(
  query?: AdminDesignQuery
): Promise<AdminDesignListResult> {
  const params = new URLSearchParams();
  if (query?.ownerUserId) params.append('ownerUserId', query.ownerUserId);
  if (query?.externalDesignerId !== undefined) {
    params.append('externalDesignerId', String(query.externalDesignerId));
  }
  if (query?.status) params.append('status', query.status);
  if (query?.createdFrom) params.append('createdFrom', query.createdFrom);
  if (query?.createdTo) params.append('createdTo', query.createdTo);
  if (query?.page) params.append('page', String(query.page));
  if (query?.limit) params.append('limit', String(query.limit));

  const response = await apiClient.get<ApiResponse<AdminDesignListResult>>(
    `/admin/designs?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Get one design of any account with products, positions, colors and mockups
 */
export async function getAdminDesign(id: string): Promise<AdminDesignDetail> {
  const response = await apiClient.get<ApiResponse<AdminDesignDetail>>(
    `/admin/designs/${encodeURIComponent(id)}`
  );
  return response.data.data;
}
